import { ResourceType } from '../api';

/** Compute the recertification cadence in days for a given resource type. */
export function cadenceDaysFor(
  configApi: {
    getOptionalNumber(key: string): number | undefined;
  },
  type: ResourceType,
  fallback = 365,
): number {
  return (
    configApi.getOptionalNumber(`recertification.cadenceDays.${type}`) ??
    fallback
  );
}

/** Days threshold within which a row is considered "due soon". */
export function dueSoonDaysFor(
  configApi: {
    getOptionalNumber(key: string): number | undefined;
  },
  fallback = 7,
): number {
  return configApi.getOptionalNumber('recertification.dueSoonDays') ?? fallback;
}

export type RowStatus =
  | { kind: 'never' }
  | { kind: 'expired'; daysOverdue: number; dueOn: Date }
  | { kind: 'dueSoon'; daysRemaining: number; dueOn: Date }
  | { kind: 'ok'; daysRemaining: number; dueOn: Date };

/**
 * Compute the client-side status of a row from its `lastRecertifiedOn`
 * timestamp, cadence and warning threshold.
 */
export function computeRowStatus(
  lastRecertifiedOn: string | null,
  cadenceDays: number,
  dueSoonDays: number,
  now: Date = new Date(),
): RowStatus {
  if (!lastRecertifiedOn) return { kind: 'never' };

  const last = new Date(lastRecertifiedOn).getTime();
  const dueOn = new Date(last + cadenceDays * 24 * 60 * 60 * 1000);
  const msRemaining = dueOn.getTime() - now.getTime();
  const daysRemaining = Math.ceil(msRemaining / (24 * 60 * 60 * 1000));

  if (msRemaining <= 0) {
    return { kind: 'expired', daysOverdue: -daysRemaining, dueOn };
  }
  if (daysRemaining <= dueSoonDays) {
    return { kind: 'dueSoon', daysRemaining, dueOn };
  }
  return { kind: 'ok', daysRemaining, dueOn };
}
